import styled from "styled-components";

/**
 * v3 lander theme (direct-mail preapproval). Same Patria styleguide tokens as
 * the other two landers, but Ink is the interactive accent — red is reserved for
 * the section kickers, the headline rule and the closing watermark.
 *
 * Scoped to this page rather than added to styles/00-settings/colors.scss: that
 * file's --color-primary is the blue LOS app palette shared with the application
 * flow, and overriding it globally would repaint the whole product.
 *
 * The form is likewise restyled here by class name, never in
 * molecules/Form/Styles/Default.tsx, whose InputWrapper is consumed by 13 field
 * components across the 8-step application flow, payments and auth.
 *
 * Sizing is px, not rem: base-font-sizing.scss drops the root from 62.5% to 50%
 * below 992px, which would shrink every rem 20% on mobile and push the inputs
 * under the 16px that stops iOS zooming on focus.
 */
export const Theme = styled.div`
  --brand-red: #d11f3d; /* primary CTAs, brand mark, key emphasis */
  --brand-red-deep: #9b1228; /* pressed states, headlines on light */
  --brand-blue: #3fa5ff;
  --brand-blue-deep: #1474d6;
  --brand-green: #22c58f; /* approval pill, success states */
  --brand-green-deep: #11906a; /* success text on light surfaces */
  --brand-ink: #0e1116; /* headlines, body copy */
  --brand-bone: #f7f6f2; /* page background alt */

  --brand-display: "Manrope", "Spartan", sans-serif;
  --brand-sans: "InterBrand", "Spartan", ui-sans-serif, system-ui, sans-serif;

  background: #fff;
  color: var(--brand-ink);
  font-family: var(--brand-sans);
  /* clip, not hidden: overflow-x:hidden makes this a scroll container, and
   * position:sticky then sticks to it instead of the page, so the header
   * never pinned. clip crops identically without the scroll container.
   * hidden stays first as a fallback for browsers without clip. */
  overflow-x: hidden;
  overflow-x: clip;

  .layout {
    background: #fff;
  }

  main {
    font-family: var(--brand-sans);
  }

  /* ---- header: sticky, translucent white, blurred ------------------------ */
  .header {
    position: sticky;
    top: 0;
    z-index: 30;
    /* rgba first as a fallback for browsers without color-mix (Chrome <111,
     * Safari <16.2); the color-mix line wins wherever it is supported */
    background: rgba(255, 255, 255, 0.8);
    background: color-mix(in oklab, #fff 80%, transparent);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    box-shadow: none;
  }

  /* the logo anchor is inline, so its box collapses instead of wrapping the
   * mark — too small a tap target on phones */
  .header .logo a {
    display: inline-block;
    line-height: 0;
  }

  .header .wrapper {
    height: auto;
    padding: 16px 0;
  }

  .header .tagline {
    display: none;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(14, 17, 22, 0.5);
  }

  .header .apply-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 16px;
    border: none;
    border-radius: 999px;
    background: var(--brand-ink);
    font-family: var(--brand-sans);
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    transition: background 0.25s;

    &:hover {
      background: var(--brand-red);
      text-decoration: none;
    }
  }

  @media screen and (min-width: 640px) {
    .header .tagline {
      display: inline;
    }
  }

  /* ---- the shared lead form, scoped -------------------------------------- */
  .form-wrapper {
    form {
      width: 100%;
      display: grid;
      /* minmax(0, …), not a bare 1fr: the captcha renders a fixed 304px box, and
       * a 1fr track takes its content as a floor, so the whole column widened to
       * 304px and pushed the fields and the submit button under the card edge —
       * cropped silently, because the page Theme sets overflow-x: clip. */
      grid-template-columns: minmax(0, 1fr);
      gap: 12px;
      row-gap: 12px;
    }

    /* the captcha container is the one classless div the widget mounts into;
     * below ~380px the card is narrower than its fixed 304px, so scale it to fit */
    @media screen and (max-width: 380px) {
      form > div:not([class]) {
        transform: scale(0.75);
        transform-origin: left top;
        height: 59px;
      }
    }

    /* the reference pairs ZIP + date of birth on one row, then stacks the rest;
     * amount + reason take that first row here */
    .fields {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 12px;
    }

    .personal-info-fields {
      grid-template-columns: 1fr;
      grid-gap: 12px;
      row-gap: 12px;
    }

    /* DOM order is ssn, email, phone; the reference shows ssn, phone, email */
    .personal-info-fields > *:nth-child(2) {
      order: 3;
    }

    /* Vertical padding only: the padding shorthand would clobber AmountWrapper's
     * padding-left of 3rem in home/Apply/Inputs.jsx and drop the "$" prefix on
     * top of the placeholder text.
     *
     * The full border shorthand also un-joins the amount + reason pair, a single
     * control app-wide. It outranks AmountWrapper/OptionsWrapper on specificity,
     * so no !important is needed — and must not be used, or it would beat the
     * :focus rule below and leave one edge grey while the others turn ink. */
    input,
    select,
    textarea {
      height: 48px;
      padding-top: 0;
      padding-bottom: 0;
      border-radius: 12px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      background: var(--brand-bone);
      font-family: var(--brand-sans);
      font-size: 16px;
      font-weight: 500;
      color: var(--brand-ink);

      &::placeholder {
        color: rgba(14, 17, 22, 0.45);
        font-weight: 500;
      }
    }

    input:focus,
    select:focus,
    textarea:focus {
      border-color: var(--brand-ink);
      background: #fff;
      box-shadow: none;
    }

    input:hover:not(:focus),
    select:hover:not(:focus) {
      box-shadow: none;
    }

    /* the shared "filled" state is gold app-wide; neutral on this page */
    .filled {
      border-color: rgba(0, 0, 0, 0.1);
      background: var(--brand-bone);
    }

    .textField-wrapper:before {
      color: var(--brand-ink);
      font-weight: 600;
      font-size: 16px;
    }

    .button-wrapper {
      display: grid;
      grid-template-columns: 1fr;
      gap: 4px;
      margin-top: 8px;
    }

    button[type="submit"] {
      width: 100%;
      min-width: unset;
      height: 56px;
      border-radius: 999px;
      background: var(--brand-ink);
      border: none;
      color: #fff;
      font-family: var(--brand-sans);
      font-size: 16px;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      box-shadow: 0 16px 30px -12px rgba(14, 17, 22, 0.55);
      transition:
        background 0.35s cubic-bezier(0.4, 0, 0.2, 1),
        box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover:not(:disabled) {
        background: var(--brand-red);
        box-shadow: 0 20px 36px -12px rgba(209, 31, 61, 0.6);
      }

      img {
        margin-left: 8px;
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
      }

      &:hover:not(:disabled) img {
        transform: translateX(10px);
      }
    }

    /* The shared DirectMailActionButton ships different copy as a bordered
     * button. It is hidden here and replaced by the reference's caption, which
     * Hero.tsx renders directly below the form.
     * !important is required: the component sets display:flex inline. */
    .button-wrapper > .button-wrapper {
      display: none !important;
    }

    .error {
      font-size: 12px;
    }
  }

  @media screen and (max-width: 480px) {
    .form-wrapper .fields {
      grid-template-columns: 1fr;
    }
  }
`;
